(() => {
  const readStorage = (key) => {
    try {
      return window.localStorage.getItem(key);
    } catch (error) {
      return null;
    }
  };

  const writeStorage = (key, value) => {
    try {
      window.localStorage.setItem(key, value);
    } catch (error) {
      // Storage can be unavailable in strict privacy modes.
    }
  };

  const applyThemeMode = (mode) => {
    const nextMode = mode === 'dark' ? 'dark' : 'light';
    document.documentElement.classList.remove('raydo-theme-light', 'raydo-theme-dark');
    document.documentElement.classList.add(`raydo-theme-${nextMode}`);
    document.documentElement.dataset.raydoTheme = nextMode;

    document.querySelectorAll('[data-raydo-theme-option]').forEach((button) => {
      const isActive = button.getAttribute('data-raydo-theme-option') === nextMode;
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });
  };

  const getGalleryThumbs = (gallery) => Array.from(gallery.querySelectorAll('[data-raydo-gallery-thumb]'));

  const syncStickyBuyBars = () => {
    const isDesktop = window.matchMedia('(min-width: 990px)').matches;

    document.querySelectorAll('[data-raydo-sticky-buy-bar]').forEach((buyBar) => {
      if (!isDesktop) {
        buyBar.style.left = '';
        buyBar.style.right = '';
        buyBar.style.width = '';
        buyBar.style.transform = '';
        return;
      }

      const shell = buyBar.closest('.raydo-product-block') || buyBar.closest('.page-width');
      if (!shell) return;

      const rect = shell.getBoundingClientRect();
      buyBar.style.left = `${Math.round(rect.left)}px`;
      buyBar.style.right = 'auto';
      buyBar.style.width = `${Math.round(rect.width)}px`;
      buyBar.style.transform = 'none';
    });
  };

  const syncGalleryRail = (gallery, mediaId, scrollBehavior = 'auto') => {
    const id = String(mediaId);
    const thumbs = getGalleryThumbs(gallery);
    const activeIndex = thumbs.findIndex((thumb) => thumb.getAttribute('data-raydo-gallery-thumb') === id);
    const activeThumb = activeIndex >= 0 ? thumbs[activeIndex] : null;

    if (activeThumb && typeof activeThumb.scrollIntoView === 'function') {
      activeThumb.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: scrollBehavior });
    }

    gallery.querySelectorAll('[data-raydo-gallery-shift]').forEach((button) => {
      const shift = Number(button.getAttribute('data-raydo-gallery-shift') || 0);
      const targetIndex = activeIndex + shift;
      button.disabled = activeIndex < 0 || targetIndex < 0 || targetIndex >= thumbs.length;
    });
  };

  const activateGalleryMedia = (gallery, mediaId, { scrollBehavior = 'smooth' } = {}) => {
    if (!gallery || !mediaId) return false;

    const id = String(mediaId);
    const items = gallery.querySelectorAll('[data-raydo-gallery-item]');
    const hasMatch = Array.from(items).some((item) => item.getAttribute('data-raydo-gallery-item') === id);

    if (!hasMatch) return false;

    gallery.querySelectorAll('[data-raydo-gallery-thumb]').forEach((thumb) => {
      const isActive = thumb.getAttribute('data-raydo-gallery-thumb') === id;
      thumb.classList.toggle('is-active', isActive);
      thumb.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });

    items.forEach((item) => {
      item.classList.toggle('is-active', item.getAttribute('data-raydo-gallery-item') === id);
    });

    syncGalleryRail(gallery, id, scrollBehavior);
    return true;
  };

  const popup = document.querySelector('[data-raydo-popup]');
  if (popup) {
    const storageKey = popup.dataset.raydoPopupKey || 'raydo-promo-popup';
    const alreadyDismissed = readStorage(storageKey);
    const delay = Number(popup.dataset.raydoPopupDelay || 1000);

    const closePopup = () => {
      popup.classList.remove('is-open');
      popup.setAttribute('hidden', '');
      writeStorage(storageKey, 'dismissed');
    };

    popup.querySelectorAll('[data-raydo-popup-close]').forEach((node) => {
      node.addEventListener('click', closePopup);
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !popup.hasAttribute('hidden')) {
        closePopup();
      }
    });

    if (!alreadyDismissed) {
      window.setTimeout(() => {
        popup.removeAttribute('hidden');
        requestAnimationFrame(() => popup.classList.add('is-open'));
      }, delay);
    }
  }

  applyThemeMode(readStorage('raydo-theme-mode') || document.documentElement.dataset.raydoTheme || 'light');
  syncStickyBuyBars();

  document.querySelectorAll('[data-raydo-theme-option]').forEach((button) => {
    button.addEventListener('click', () => {
      const mode = button.getAttribute('data-raydo-theme-option');
      writeStorage('raydo-theme-mode', mode);
      applyThemeMode(mode);
      button.closest('[data-raydo-theme-menu]')?.removeAttribute('open');
    });
  });

  document.addEventListener('click', (event) => {
    document.querySelectorAll('[data-raydo-theme-menu][open]').forEach((menu) => {
      if (!menu.contains(event.target)) {
        menu.removeAttribute('open');
      }
    });
  });

  let stickyBuyBarFrame = null;
  const queueStickyBuyBarsSync = () => {
    if (stickyBuyBarFrame) cancelAnimationFrame(stickyBuyBarFrame);
    stickyBuyBarFrame = requestAnimationFrame(() => {
      stickyBuyBarFrame = null;
      syncStickyBuyBars();
    });
  };

  window.addEventListener('resize', queueStickyBuyBarsSync);
  window.addEventListener('orientationchange', queueStickyBuyBarsSync);
  window.addEventListener('load', queueStickyBuyBarsSync);

  document.querySelectorAll('[data-raydo-gallery]').forEach((gallery) => {
    const thumbs = getGalleryThumbs(gallery);

    thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => {
        activateGalleryMedia(gallery, thumb.getAttribute('data-raydo-gallery-thumb'));
      });
    });

    gallery.querySelectorAll('[data-raydo-gallery-shift]').forEach((button) => {
      button.addEventListener('click', () => {
        const orderedThumbs = getGalleryThumbs(gallery);
        const activeId =
          gallery.querySelector('[data-raydo-gallery-thumb].is-active')?.getAttribute('data-raydo-gallery-thumb') ||
          orderedThumbs[0]?.getAttribute('data-raydo-gallery-thumb');
        const activeIndex = orderedThumbs.findIndex((thumb) => thumb.getAttribute('data-raydo-gallery-thumb') === activeId);
        const shift = Number(button.getAttribute('data-raydo-gallery-shift') || 0);
        const nextThumb = orderedThumbs[activeIndex + shift];

        if (nextThumb) {
          activateGalleryMedia(gallery, nextThumb.getAttribute('data-raydo-gallery-thumb'));
        }
      });
    });

    const initialId =
      gallery.querySelector('[data-raydo-gallery-thumb].is-active')?.getAttribute('data-raydo-gallery-thumb') ||
      thumbs[0]?.getAttribute('data-raydo-gallery-thumb');

    if (initialId) {
      activateGalleryMedia(gallery, initialId, { scrollBehavior: 'auto' });
    }
  });

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.variantChange, ({ data } = {}) => {
      const mediaId = data?.variant?.featured_media?.id;
      if (!mediaId) return;

      const scope = data?.sectionId
        ? document.querySelector(`[data-section="${data.sectionId}"]`) || document
        : document;

      scope.querySelectorAll('[data-raydo-gallery]').forEach((gallery) => {
        activateGalleryMedia(gallery, mediaId);
      });

      queueStickyBuyBarsSync();
    });
  }

  document.querySelectorAll('[data-copy-text]').forEach((button) => {
    const originalMeta = button.querySelector('.raydo-copy-pill__meta');
    const originalMetaText = originalMeta?.textContent || 'Copy';

    button.addEventListener('click', async () => {
      const text = button.getAttribute('data-copy-text');
      if (!text) return;

      try {
        await navigator.clipboard.writeText(text);
        button.classList.add('is-copied');
        if (originalMeta) {
          originalMeta.textContent = button.getAttribute('data-copy-success') || 'Copied';
        }
        window.setTimeout(() => {
          button.classList.remove('is-copied');
          if (originalMeta) {
            originalMeta.textContent = originalMetaText;
          }
        }, 1800);
      } catch (error) {
        console.warn('Raydo copy failed', error);
      }
    });
  });

  document.querySelectorAll('[data-raydo-quantity]').forEach((quantity) => {
    const input = quantity.querySelector('.raydo-product-quantity__input');
    const minus = quantity.querySelector('[data-raydo-quantity-minus]');
    const plus = quantity.querySelector('[data-raydo-quantity-plus]');

    if (!input) return;

    const min = () => Number(input.getAttribute('min') || 1);
    const normalize = () => {
      const value = Math.max(min(), Number(input.value || min()));
      input.value = String(Number.isFinite(value) ? value : min());
      minus?.toggleAttribute('disabled', Number(input.value) <= min());
    };

    minus?.addEventListener('click', () => {
      input.value = String(Math.max(min(), Number(input.value || min()) - 1));
      input.dispatchEvent(new Event('change', { bubbles: true }));
      normalize();
    });

    plus?.addEventListener('click', () => {
      input.value = String(Math.max(min(), Number(input.value || min()) + 1));
      input.dispatchEvent(new Event('change', { bubbles: true }));
      normalize();
    });

    input.addEventListener('change', normalize);
    input.addEventListener('blur', normalize);
    normalize();
  });

  const stickyLinks = Array.from(document.querySelectorAll('[data-raydo-sticky-link]'));
  const stickyTargets = stickyLinks
    .map((link) => {
      const id = link.hash?.slice(1);
      const target = id ? document.getElementById(id) : null;
      return target ? { link, target } : null;
    })
    .filter(Boolean);

  if (stickyTargets.length) {
    const setActiveStickyLink = (activeLink) => {
      stickyLinks.forEach((link) => link.classList.toggle('is-active', link === activeLink));
    };

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        (entries) => {
          const visibleEntry = entries
            .filter((entry) => entry.isIntersecting)
            .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];

          if (!visibleEntry) return;

          const match = stickyTargets.find(({ target }) => target === visibleEntry.target);
          if (match) setActiveStickyLink(match.link);
        },
        {
          rootMargin: '-38% 0px -50% 0px',
          threshold: [0, 0.2, 0.6],
        }
      );

      stickyTargets.forEach(({ target }) => observer.observe(target));
    }

    setActiveStickyLink(stickyTargets[0].link);
  }
})();
